/* ============================================================
   Baby Cubby — Car-Seat PDP behaviors
   Vanilla, no build step. Ported from the prototype's React hooks.
   Everything is defensive: each initializer no-ops when its markup
   isn't present, so the file is safe to load site-wide. Re-inits on
   Shopify theme-editor section loads.
   ============================================================ */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function once(el, key) { if (!el || el.dataset['pdp_' + key]) return false; el.dataset['pdp_' + key] = '1'; return true; }

  /* ---------- reveal on scroll ---------- */
  function initReveal(root) {
    var els = $all('.pdp-cs .reveal', root).filter(function (el) { return once(el, 'reveal'); });
    if (!els.length) return;
    if (prefersReduced || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.remove('pre'); e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.01 });
    els.forEach(function (el) { el.classList.add('pre'); io.observe(el); });
    // safety: force-reveal anything still hidden after 2.5s
    setTimeout(function () { els.forEach(function (el) { if (!el.classList.contains('in')) { el.classList.remove('pre'); el.classList.add('in'); } }); }, 2500);
  }

  /* ---------- accordions (buy-column, FAQ, love) ---------- */
  /* The FAQ names are `cs-`-prefixed and must stay in lockstep with
     assets/pdp-car-seat.css. The group selector deliberately does NOT match a
     bare `.accordion`: that is Dawn's own buy-box <details> class, which opens
     natively and needs no JS — matching it only bound a listener that no-oped
     (Dawn uses <summary>, not .acc-head). Same collision the CSS note describes. */
  function setBody(item, open) {
    var body = item.querySelector('.cs-acc-body, .col-acc-body');
    if (!body) return;
    if (open) { body.style.maxHeight = body.scrollHeight + 'px'; }
    else { body.style.maxHeight = '0px'; }
  }
  function initAccordions(root) {
    $all('.pdp-cs .cs-accordion, .pdp-cs .col-acc', root).forEach(function (group) {
      if (!once(group, 'acc')) return;
      // An item rendered open carries the design's inline max-height (360px), so it
      // paints open with no flash before this runs. Raise it only when the answer is
      // taller, so long copy can't clip; shorter answers are untouched and unchanged
      // either way (max-height sets a ceiling, not a height).
      $all('.cs-acc-item.open, .col-acc-item.open', group).forEach(function (o) {
        var body = o.querySelector('.cs-acc-body, .col-acc-body');
        if (body && body.scrollHeight > body.clientHeight) setBody(o, true);
      });
      group.addEventListener('click', function (ev) {
        var head = ev.target.closest('.cs-acc-head, .col-acc-head');
        if (!head || !group.contains(head)) return;
        var item = head.closest('.cs-acc-item, .col-acc-item');
        var isOpen = item.classList.contains('open');
        // One open at a time. The exclusive scope defaults to this group, but an
        // ancestor marked [data-acc-exclusive] widens it: the design's FAQ holds a
        // single open key for ALL categories (useState("0-0") keyed "cat-question"),
        // so opening one closes the open item in every other category too — its
        // .cs-accordion-per-category grouping would otherwise allow five at once.
        var scope = group.closest('[data-acc-exclusive]') || group;
        $all('.cs-acc-item.open, .col-acc-item.open', scope).forEach(function (o) {
          if (o !== item) { o.classList.remove('open'); setBody(o, false); o.querySelector('.cs-acc-head, .col-acc-head').setAttribute('aria-expanded', 'false'); }
        });
        item.classList.toggle('open', !isOpen);
        setBody(item, !isOpen);
        head.setAttribute('aria-expanded', String(!isOpen));
      });
    });
    // mobile trust "see more"
    $all('.pdp-cs .t4-more', root).forEach(function (btn) {
      if (!once(btn, 't4')) return;
      btn.addEventListener('click', function () {
        var card = btn.closest('.t4-card');
        if (card) { var open = card.classList.toggle('open'); btn.setAttribute('aria-expanded', String(open)); }
      });
    });
  }

  /* ---------- FAQ sticky nav ---------- */
  function initFaqNav(root) {
    $all('.pdp-cs .faq-grid', root).forEach(function (grid) {
      if (!once(grid, 'faqnav')) return;
      var buttons = $all('.faq-nav button', grid);
      var cats = $all('.faq-cat', grid);
      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var target = grid.querySelector('#' + btn.getAttribute('data-faq-target'));
          if (target) target.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
          buttons.forEach(function (b) { b.classList.remove('on'); });
          btn.classList.add('on');
        });
      });
      if ('IntersectionObserver' in window && cats.length) {
        var spy = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              var id = e.target.id;
              buttons.forEach(function (b) { b.classList.toggle('on', b.getAttribute('data-faq-target') === id); });
            }
          });
        }, { rootMargin: '-30% 0px -60% 0px' });
        cats.forEach(function (c) { spy.observe(c); });
      }
    });
  }

  /* ---------- video modal ---------- */
  // `video` is a YouTube id string (the original signature — the desktop grid
  // and gallery lightbox still pass that) or {yt, embed, src}: yt = YouTube id,
  // embed = full embed URL (non-YouTube hosts), src = hosted mp4 → native
  // <video>. The mobile video row feeds all three from the product gallery.
  function openVideoModal(root, video) {
    var opts = typeof video === 'string' ? { yt: video } : video || {};
    var allow = 'allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowfullscreen';
    var media;
    if (opts.yt) {
      media = '<iframe ' + allow + ' src="https://www.youtube.com/embed/' + opts.yt + '?autoplay=1&rel=0"></iframe>';
    } else if (opts.embed) {
      media = '<iframe ' + allow + ' src="' + opts.embed + '"></iframe>';
    } else if (opts.src) {
      media = '<video controls autoplay playsinline src="' + opts.src + '"></video>';
    } else return;
    var mount = root.querySelector('.pdp-cs') || root;
    var modal = document.createElement('div');
    modal.className = 'modal';
    modal.innerHTML =
      '<button class="close" aria-label="Close video">' + icon('x') + '</button>' +
      '<div class="frame">' + media + '</div>';
    function close() { modal.remove(); document.removeEventListener('keydown', onKey); }
    function onKey(e) { if (e.key === 'Escape') close(); }
    modal.addEventListener('click', function (e) { if (e.target === modal || e.target.closest('.close')) close(); });
    document.addEventListener('keydown', onKey);
    mount.appendChild(modal);
  }
  function initVideoButtons(root) {
    $all('.pdp-cs [data-pdp-video], .pdp-cs [data-pdp-video-embed], .pdp-cs [data-pdp-video-src]', root).forEach(function (btn) {
      if (!once(btn, 'vid')) return;
      btn.addEventListener('click', function () {
        openVideoModal(btn.closest('.pdp-cs') || document, {
          yt: btn.getAttribute('data-pdp-video'),
          embed: btn.getAttribute('data-pdp-video-embed'),
          src: btn.getAttribute('data-pdp-video-src')
        });
      });
    });
  }

  /* ---------- lightbox (image modal) ---------- */
  function openLightbox(gal, items, startIndex) {
    var imgs = items.filter(function (it) { return !it.video && it.full; });
    if (!imgs.length) return;
    var idx = Math.max(0, imgs.findIndex(function (it) { return it === items[startIndex]; }));
    if (idx < 0) idx = 0;
    var mount = gal.closest('.pdp-cs') || gal;
    var lb = document.createElement('div');
    lb.className = 'lightbox';
    // Nav arrows are direct children of .lightbox (not .lb-stage) so their
    // left/right:14px anchors to the fixed overlay edges on the dark backdrop —
    // nesting them in the shrink-wrapped stage put white arrows over white images.
    lb.innerHTML =
      '<button class="lb-close" aria-label="Close">' + icon('x') + '</button>' +
      '<div class="lb-count"></div>' +
      '<button class="lb-nav lb-prev" aria-label="Previous">' + icon('chevronLeft') + '</button>' +
      '<div class="lb-stage">' +
        '<img class="lb-img zoom-out" alt="">' +
        '<div class="lb-cap"></div>' +
        '<div class="lb-zoomhint">' + icon('search') + ' Click to zoom</div>' +
      '</div>' +
      '<button class="lb-nav lb-next" aria-label="Next">' + icon('chevronRight') + '</button>' +
      '<div class="lb-strip"></div>';
    var stage = lb.querySelector('.lb-stage');
    var img = lb.querySelector('.lb-stage img');
    var cap = lb.querySelector('.lb-cap');
    var hint = lb.querySelector('.lb-zoomhint');
    var count = lb.querySelector('.lb-count');
    var strip = lb.querySelector('.lb-strip');
    var zoomed = false;
    imgs.forEach(function (it, k) {
      var b = document.createElement('button');
      b.className = 'lb-thumb'; b.innerHTML = '<img src="' + it.full + '" alt="">';
      b.addEventListener('click', function () { idx = k; render(); });
      strip.appendChild(b);
    });
    // Click-zoom (design: 2.5× scale, transform-origin follows the cursor,
    // mousemove pans; caption + hint hidden while zoomed). Mouse-driven — on
    // touch a tap sets a fixed origin (no live pan), which degrades gracefully.
    function originFrom(e) {
      var r = img.getBoundingClientRect();
      var x = ((e.clientX - r.left) / r.width) * 100;
      var y = ((e.clientY - r.top) / r.height) * 100;
      return x + '% ' + y + '%';
    }
    function setZoom(on, origin) {
      zoomed = on;
      img.style.transformOrigin = zoomed ? (origin || '50% 50%') : '';
      img.style.transform = zoomed ? 'scale(2.5)' : '';
      img.classList.toggle('zoom-in', zoomed);
      img.classList.toggle('zoom-out', !zoomed);
      stage.classList.toggle('zoomed', zoomed);
      hint.style.display = zoomed ? 'none' : '';
      var it = imgs[idx];
      cap.style.display = (it && it.cap && !zoomed) ? '' : 'none';
    }
    img.addEventListener('click', function (e) { e.stopPropagation(); setZoom(!zoomed, originFrom(e)); });
    img.addEventListener('mousemove', function (e) { if (zoomed) img.style.transformOrigin = originFrom(e); });
    function render() {
      var it = imgs[idx];
      img.src = it.full; cap.textContent = it.cap || '';
      count.textContent = (idx + 1) + ' / ' + imgs.length;
      $all('.lb-thumb', strip).forEach(function (t, k) { t.classList.toggle('active', k === idx); });
      setZoom(false); // any image change (nav / thumb / arrow keys) exits zoom
    }
    function close() { lb.remove(); document.removeEventListener('keydown', onKey); }
    function onKey(e) {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') { idx = (idx + 1) % imgs.length; render(); }
      else if (e.key === 'ArrowLeft') { idx = (idx - 1 + imgs.length) % imgs.length; render(); }
    }
    lb.addEventListener('click', function (e) { if (e.target === lb || e.target.closest('.lb-close')) close(); });
    lb.querySelector('.lb-prev').addEventListener('click', function () { idx = (idx - 1 + imgs.length) % imgs.length; render(); });
    lb.querySelector('.lb-next').addEventListener('click', function () { idx = (idx + 1) % imgs.length; render(); });
    document.addEventListener('keydown', onKey);
    mount.appendChild(lb); render();
  }

  /* ---------- image modal: wire Dawn's gallery to the custom lightbox ---------- */
  function initGalleryModal(root) {
    $all('.product--car-seat media-gallery', root).forEach(function (gallery) {
      if (!once(gallery, 'lightbox')) return;
      var section = gallery.closest('[data-section]') || gallery.closest('.pdp-cs');
      var dataEl = section && section.querySelector('[data-pdp-media]');
      var items;
      try { items = JSON.parse(dataEl ? dataEl.textContent : '[]'); } catch (e) { items = []; }
      if (!items.length) return;
      var byId = {};
      items.forEach(function (it) { byId[String(it.id)] = it; });

      // Capture phase so we beat Dawn's modal-opener button handler and can
      // preventDefault/stopPropagation before its native ProductModal opens.
      gallery.addEventListener('click', function (e) {
        var li = e.target.closest('.product__media-item');
        if (!li || !gallery.contains(li)) return;
        var toggle = li.querySelector('.product__media-toggle');
        var mediaId = (toggle && toggle.getAttribute('data-media-id')) ||
          (li.getAttribute('data-media-id') || '').split('-').pop();
        var it = byId[String(mediaId)];
        if (!it) return; // unknown media → let Dawn handle it
        if (it.video) { // external YouTube → video modal
          e.preventDefault(); e.stopPropagation();
          openVideoModal(gallery.closest('.pdp-cs') || document, it.video);
          return;
        }
        if (it.type && it.type !== 'image') return; // hosted video / 3D model → keep Dawn's native handling
        e.preventDefault(); e.stopPropagation();
        // per-color gallery: the lightbox shows only the active color's shots +
        // the common media, in that order (initColorGallery keeps the attribute
        // current; without grouping the attribute is absent → full flat list)
        var activeColor = gallery.getAttribute('data-cs-active-color');
        var pool = items;
        if (activeColor) {
          var shots = [], commons = [];
          items.forEach(function (x) {
            if (x.group === activeColor) shots.push(x);
            else if (!x.group) commons.push(x);
          });
          // hero (first visible tile, i.e. the variant's featured media) leads,
          // matching the on-page order
          var heroLi = gallery.querySelector('.product__media-item');
          var heroId = heroLi ? (heroLi.getAttribute('data-media-id') || '').split('-').pop() : '';
          shots.sort(function (a, b) {
            return (String(b.id) === heroId) - (String(a.id) === heroId);
          });
          pool = shots.concat(commons);
        }
        var idx = pool.indexOf(it);
        openLightbox(gallery, pool, idx < 0 ? 0 : idx);
      }, true);
    });
  }

  /* ---------- gallery "show all photos" (collapses tall grids on desktop) ---------- */
  function initPhotoGrid(root) {
    $all('.product--car-seat media-gallery', root).forEach(function (gallery) {
      var list = gallery.querySelector('.product__media-list');
      if (!list || !once(list, 'photogrid')) return;
      var btn = gallery.querySelector('[data-cs-show-all]');
      if (!btn) return;
      var textEl = btn.querySelector('.cs-show-all-text');
      var LIMIT = 6; // grid tiles shown when collapsed (hero not counted)
      // grid starts where the design's mobile slider ends (640px, delta 1) —
      // keep in sync with section-main-product-car-seat.css
      var mq = window.matchMedia('(min-width: 640.02px)');
      var expanded = false;

      // visible media items minus the hero (first visible one);
      // display:none variant duplicates fall out via offsetParent.
      function tiles() {
        return $all('.product__media-item', list).filter(function (el) {
          return el.offsetParent !== null;
        }).slice(1);
      }

      function apply() {
        $all('.product__media-item.cs-tile-hidden', list).forEach(function (el) { el.classList.remove('cs-tile-hidden'); });
        if (!mq.matches) { btn.classList.remove('is-active'); return; } // mobile keeps the swipe slider
        var t = tiles();
        if (t.length <= LIMIT) { btn.classList.remove('is-active'); return; }
        btn.classList.add('is-active');
        btn.classList.toggle('is-expanded', expanded);
        btn.setAttribute('aria-expanded', String(expanded));
        if (textEl) textEl.textContent = expanded ? 'Show fewer photos' : ('Show all ' + (t.length + 1) + ' photos');
        if (!expanded) { t.slice(LIMIT).forEach(function (el) { el.classList.add('cs-tile-hidden'); }); }
      }

      btn.addEventListener('click', function () { expanded = !expanded; apply(); });

      // the list reorders on variant change (global.js prepends the featured image)
      if ('MutationObserver' in window) {
        new MutationObserver(function () { apply(); }).observe(list, { childList: true });
      }
      var rt;
      window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(apply, 150); });
      apply();
    });
  }

  /* ---------- per-color gallery (June handoff delta 1) ---------- */
  // The design's galleryFor(colorIdx): selecting a color swaps the whole gallery —
  // that color's shots first (variant featured media leading), then the shared
  // common media; other colors' media hidden. Groups are computed server-side
  // (variant attachment, else alt-text color match) and carried in the
  // data-pdp-media payload; this keeps the DOM in the same order the section
  // renders initially. Dawn's updateMedia() prepend still runs first on variant
  // change — the regroup rebuilds the full order after it, so they agree.
  function initColorGallery(root) {
    $all('.product--car-seat media-gallery', root).forEach(function (gallery) {
      if (!once(gallery, 'colorgallery')) return;
      var section = gallery.closest('[data-section]') || gallery.closest('.pdp-cs');
      if (!section) return;
      var colorsEl = section.querySelector('[data-pdp-variant-colors]');
      var mediaEl = section.querySelector('[data-pdp-media]');
      if (!colorsEl || !mediaEl) return; // no color grouping on this product
      var vmap, items;
      try {
        vmap = JSON.parse(colorsEl.textContent);
        items = JSON.parse(mediaEl.textContent);
      } catch (e) { return; }
      var list = gallery.querySelector('.product__media-list');
      var picker = section.querySelector('variant-radios, variant-selects');
      var idInput = section.querySelector('product-form input[name="id"]');
      if (!list || !picker || !idInput) return;

      var liById = {};
      $all('.product__media-item', list).forEach(function (li) {
        liById[(li.getAttribute('data-media-id') || '').split('-').pop()] = li;
      });

      function regroup(color, featId) {
        var shots = [], commons = [], hidden = [];
        items.forEach(function (it) {
          var li = liById[String(it.id)];
          if (!li) return;
          var g = it.group || '';
          if (String(it.id) === String(featId)) shots.unshift(li); // hero leads
          else if (g === color) shots.push(li);
          else if (!g) commons.push(li);
          else hidden.push(li);
        });
        shots.concat(commons, hidden).forEach(function (li) { list.appendChild(li); });
        items.forEach(function (it) {
          var li = liById[String(it.id)];
          if (!li) return;
          var g = it.group || '';
          li.classList.toggle('cs-media-hidden', !!g && g !== color);
        });
        gallery.setAttribute('data-cs-active-color', color);
        var viewer = gallery.querySelector('slider-component');
        if (viewer && typeof viewer.resetPages === 'function') viewer.resetPages();
        if (list.scrollTo) list.scrollTo({ left: 0 }); // mobile slider back to slide 1
      }

      var lastColor = null;
      var initial = vmap[idInput.value];
      if (initial) lastColor = initial.c; // server render already matches
      function update() {
        var v = vmap[idInput.value];
        if (!v || !v.c || v.c === lastColor) return;
        lastColor = v.c;
        regroup(v.c, v.m || 0);
      }
      // Dawn's VariantSelects change handler (incl. the hidden-id update) is
      // registered first on the same element, so ours runs after it in the same
      // task; the timeout is belt-and-braces against listener-order surprises.
      picker.addEventListener('change', function () { update(); setTimeout(update, 0); });
    });
  }

  /* ---------- mobile slider dash indicators (design .slider-dots) ---------- */
  function initMediaDashes(root) {
    $all('.product--car-seat media-gallery', root).forEach(function (gallery) {
      var list = gallery.querySelector('.product__media-list');
      var dots = gallery.querySelector('[data-cs-dots]');
      if (!list || !dots || !once(dots, 'dashes')) return;
      var slides = [];

      function current() {
        return list.clientWidth ? Math.round(list.scrollLeft / list.clientWidth) : 0;
      }
      function paint() {
        var i = Math.min(current(), slides.length - 1);
        var active = null;
        $all('.cs-dash', dots).forEach(function (d, k) {
          d.classList.toggle('on', k === i);
          if (k === i) active = d;
        });
        // single-row strip: keep the active dash centered once the row
        // overflows (partial dashes at the edges signal more slides)
        if (active && dots.scrollWidth > dots.clientWidth) {
          var target = active.getBoundingClientRect().left - dots.getBoundingClientRect().left +
            dots.scrollLeft - (dots.clientWidth - active.offsetWidth) / 2;
          if (dots.scrollTo) dots.scrollTo({ left: target, behavior: 'smooth' });
          else dots.scrollLeft = target;
        }
      }
      function rebuild() {
        // cs-video-slide: on mobile (the only widths the dashes render at)
        // video media live in the .cs-mobile-vid-row below, not the slider
        slides = $all('.product__media-item', list).filter(function (el) {
          return !el.classList.contains('cs-media-hidden') &&
            !el.classList.contains('cs-video-slide');
        });
        dots.textContent = '';
        if (slides.length < 2) return; // single slide → no indicators
        slides.forEach(function (s, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'cs-dash';
          b.setAttribute('aria-label', 'Photo ' + (i + 1));
          b.addEventListener('click', function () {
            list.scrollTo({ left: i * list.clientWidth, behavior: 'smooth' });
          });
          dots.appendChild(b);
        });
        paint();
      }

      list.addEventListener('scroll', function () { requestAnimationFrame(paint); }, { passive: true });
      var rt;
      window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(paint, 150); });
      // regrouping reorders/toggles the slides — rebuild (debounced) on any change
      if ('MutationObserver' in window) {
        var mt;
        new MutationObserver(function () { clearTimeout(mt); mt = setTimeout(rebuild, 60); })
          .observe(list, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      }
      rebuild();
    });
  }

  /* ---------- sticky add-to-bag ---------- */
  function initStickyBar(root) {
    $all('.pdp-cs [data-pdp-stickybar]', root).forEach(function (bar) {
      if (!once(bar, 'sticky')) return;
      var anchor = document.querySelector('[data-pdp-cta-anchor]');
      if (!anchor || !('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { bar.classList.toggle('show', !e.isIntersecting && e.boundingClientRect.top < 0); });
      }, { threshold: 0 });
      io.observe(anchor);
    });
  }

  /* ---------- urgency: countdown / toasts ---------- */
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function initCountdowns(root) {
    $all('.pdp-cs [data-pdp-countdown]', root).forEach(function (el) {
      if (!once(el, 'count')) return;
      var hour = parseInt(el.getAttribute('data-cutoff-hour') || '14', 10);
      function tick() {
        var now = new Date();
        var cutoff = new Date(now); cutoff.setHours(hour, 0, 0, 0);
        if (cutoff <= now) cutoff.setDate(cutoff.getDate() + 1);
        var s = Math.floor((cutoff - now) / 1000);
        var h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
        el.textContent = pad(h) + 'h ' + pad(m) + 'm ' + pad(sec) + 's';
      }
      tick(); setInterval(tick, 1000);
    });
  }
  function initToasts(root) {
    var src = $('.pdp-cs [data-pdp-toasts]', root);
    if (!src || !once(src, 'toasts')) return;
    var people;
    try { people = JSON.parse(src.getAttribute('data-pdp-toasts') || '[]'); } catch (e) { people = []; }
    if (!people.length) return;
    var mount = src.closest('.pdp-cs') || document.body;
    var box = document.createElement('div'); box.className = 'toasts'; mount.appendChild(box);
    var i = 0;
    function show() {
      var p = people[i % people.length]; i++;
      var t = document.createElement('div'); t.className = 'toast';
      t.innerHTML = (p.img ? '<img src="' + p.img + '" alt="">' : '') +
        '<div class="tt"><b>' + (p.name || 'Someone') + '</b> in ' + (p.location || 'the US') + '<br>bought this ' + (p.when || 'recently') + '</div>';
      box.appendChild(t);
      setTimeout(function () { t.style.opacity = '0'; t.style.transition = 'opacity .4s'; setTimeout(function () { t.remove(); }, 420); }, 3500);
    }
    setTimeout(function tickToast() { show(); setTimeout(tickToast, 9000 + Math.random() * 8000); }, 4000);
  }

  /* ---------- in-stock row (per-variant availability) ---------- */
  // buy_buttons isn't re-rendered on variant change, so keep the .instock-row
  // in sync by reading the product form's hidden variant id against an
  // id->available map emitted server-side. CSS swaps the check/clock icon on
  // `.oos`; we only toggle the class + text.
  function initInstockRow(root) {
    $all('.pdp-cs [data-pdp-instock]', root).forEach(function (row) {
      if (!once(row, 'instock')) return;
      var container = row.closest('.product__info-container') || root;
      var mapEl = $('[data-pdp-variants]', container);
      if (!mapEl) return; // single-variant product: static server render is correct
      var map = {};
      try { map = JSON.parse(mapEl.textContent); } catch (e) { return; }
      var picker = $('variant-radios, variant-selects', container);
      if (!picker) return;
      var idInput = $('product-form input[name="id"]', container);
      var txt = $('[data-instock-text]', row);
      function update() {
        var id = idInput && idInput.value;
        if (id == null || !(id in map)) return;
        var avail = !!map[id];
        row.classList.toggle('oos', !avail);
        if (txt) txt.textContent = avail ? 'In Stock Today' : 'Currently out of stock';
      }
      // run after Dawn's own change handler has updated the hidden id input
      picker.addEventListener('change', function () { setTimeout(update, 0); });
      update();
    });
  }

  /* ---------- final CTA (second buy path, bottom of the page) ---------- */
  // The final CTA is its own section, so its Liquid can't know the main
  // section's form id. Resolve Dawn's product form at init and wire the button
  // via the native `form=` association (the stickybar mechanism) — the browser
  // then submits the main form itself: same quantity, same line-item
  // properties, same Dawn AJAX handler, no second form to collide with the buy
  // box or stickybar. The price/rewards line is per-variant and Dawn never
  // re-renders other sections on variant change, so sync it (and the disabled
  // state) from the form's hidden id against the section's
  // id -> { price, available } map — the initInstockRow pattern.
  function initFinalCta(root) {
    $all('.pdp-cs [data-pdp-finalcta]', root).forEach(function (band) {
      if (!once(band, 'finalcta')) return;
      var form = document.querySelector('.pdp-cs product-form form[data-type="add-to-cart-form"]');
      var btn = $('[data-pdp-finalcta-add]', band);
      // getAttribute, not form.id: the form's <input name="id"> (Dawn's variant
      // id field) shadows the `id` property on the form-elements namespace.
      var formId = form && form.getAttribute('id');
      if (btn && formId) btn.setAttribute('form', formId);

      var tag = $('[data-pdp-finalcta-price]', band);
      var mapEl = $('[data-pdp-finalcta-variants]', band);
      if (!tag || !mapEl || !form) return; // single-variant product: static server render is correct
      var map;
      try { map = JSON.parse(mapEl.textContent); } catch (e) { return; }
      var picker = document.querySelector('.pdp-cs variant-radios, .pdp-cs variant-selects');
      var idInput = $('input[name="id"]', form);
      if (!picker || !idInput) return;

      var currency = band.getAttribute('data-currency') || 'USD';
      var note = tag.getAttribute('data-note') || '';
      var rewardTpl = tag.getAttribute('data-reward-template') || '';
      var pct = parseFloat(tag.getAttribute('data-reward-pct')) || 0;

      function money(cents) {
        // match Liquid money_without_trailing_zeros: whole amounts drop cents
        var frac = cents % 100 ? 2 : 0;
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency, minimumFractionDigits: frac, maximumFractionDigits: frac }).format(cents / 100);
        } catch (e) { return '$' + (cents / 100).toFixed(frac); }
      }
      function update() {
        var v = map[idInput.value];
        if (!v) return;
        var parts = [money(v.price)];
        if (note) parts.push(note);
        if (pct > 0 && rewardTpl) {
          // design: Math.round(price * pct/100) in whole dollars
          var rewardCents = Math.round(v.price * pct / 10000) * 100;
          parts.push(rewardTpl.replace('[amount]', money(rewardCents)));
        }
        tag.textContent = parts.join(' · ');
        if (btn) btn.disabled = !v.available;
      }
      // run after Dawn's own change handler has updated the hidden id input
      picker.addEventListener('change', function () { setTimeout(update, 0); });
      update();
    });
  }

  /* ---------- accordion jump links ---------- */
  // Collapsible rows can carry a "jump to section" button (design: .col-acc-jump).
  // Buttons render [hidden] and are revealed only when their target id exists, so
  // rows can be configured before the Phase-2/3/4 target sections are built.
  // Ported behavior: 480ms eased scroll, -70px header offset, 1.6s flash pulse.
  function initJumpLinks(root) {
    $all('.pdp-cs [data-pdp-jump]', root).forEach(function (btn) {
      var target = document.getElementById(btn.getAttribute('data-pdp-jump'));
      if (!target) return;
      btn.hidden = false;
      if (!once(btn, 'jump')) return;
      btn.addEventListener('click', function () {
        var end = target.getBoundingClientRect().top + window.scrollY - 70;
        var start = window.scrollY, t0 = null, DUR = 480;
        function ease(p) { return p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2; }
        function step(ts) {
          if (t0 === null) t0 = ts;
          var p = Math.min(1, (ts - t0) / DUR);
          window.scrollTo(0, start + (end - start) * ease(p));
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
        target.classList.remove('jump-flash');
        void target.offsetWidth;
        target.classList.add('jump-flash');
        setTimeout(function () { target.classList.remove('jump-flash'); }, 1600);
      });
    });
  }

  /* ---------- curated rail: complementary products (delta 3) ----------
     Fetches Search & Discovery complementary picks (fallback: algorithmic
     "related" while the client hasn't assigned any) and prepends them to the
     server-rendered favorites — complementary first, de-duped by handle,
     capped at data-max. Band starts [hidden] when there are no favorites. */
  function initCuratedRail(root) {
    $all('.pdp-cs [data-pdp-curated]', root).forEach(function (band) {
      if (!once(band, 'curated')) return;
      var track = band.querySelector('.col-cur-track');
      var url = band.getAttribute('data-url');
      var pid = band.getAttribute('data-product-id');
      if (!track || !url || !pid || !window.fetch) return;
      var max = parseInt(band.getAttribute('data-max'), 10) || 15;
      var currency = band.getAttribute('data-currency') || 'USD';

      function money(cents) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency }).format(cents / 100);
        } catch (e) { return '$' + (cents / 100).toFixed(2); }
      }
      function fetchIntent(intent) {
        return fetch(url + '?product_id=' + pid + '&limit=10&intent=' + intent)
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) { return (d && d.products) || []; })
          .catch(function () { return []; });
      }
      function card(p) {
        var a = document.createElement('a');
        a.className = 'col-cur-card';
        a.href = p.url;
        a.setAttribute('data-handle', p.handle);
        var ph = document.createElement('span');
        ph.className = 'ph';
        if (p.featured_image) {
          var img = document.createElement('img');
          img.src = p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=200';
          img.alt = p.title;
          img.loading = 'lazy';
          img.width = 200;
          img.height = 200;
          ph.appendChild(img);
        }
        var b = document.createElement('b');
        b.textContent = p.title;
        var price = document.createElement('span');
        price.className = 'p';
        price.textContent = money(p.price);
        a.appendChild(ph); a.appendChild(b); a.appendChild(price);
        return a;
      }

      fetchIntent('complementary')
        .then(function (products) { return products.length ? products : fetchIntent('related'); })
        .then(function (products) {
          var frag = document.createDocumentFragment(), added = 0;
          products.forEach(function (p) {
            if (!p.available || !p.handle) return;
            var dup = track.querySelector('.col-cur-card[data-handle="' + p.handle + '"]');
            if (dup) dup.remove(); // complementary position (first) wins over the favorite copy
            frag.appendChild(card(p));
            added++;
          });
          if (!added) return;
          track.insertBefore(frag, track.firstChild);
          while (track.children.length > max) track.removeChild(track.lastChild);
          track.scrollLeft = 0; // scroll anchoring would otherwise keep the old first card in view
          band.hidden = false;
        });
    });
  }

  /* ---------- curated carousel arrows ---------- */
  function initCarousels(root) {
    $all('.pdp-cs [data-pdp-carousel]', root).forEach(function (wrap) {
      if (!once(wrap, 'carousel')) return;
      var track = wrap.querySelector('.col-cur-track, .col-rev-track');
      if (!track) return;
      $all('.col-cur-arrow', wrap).forEach(function (arr) {
        arr.addEventListener('click', function () {
          var dir = arr.classList.contains('left') ? -1 : 1;
          track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
        });
      });
    });
  }

  /* ---------- tiny inline icon set (only what JS injects) ---------- */
  function icon(name) {
    var p = {
      x: '<path d="M6 6l12 12M18 6L6 18"/>',
      chevronLeft: '<path d="M15 6l-6 6 6 6"/>',
      chevronRight: '<path d="M9 6l6 6-6 6"/>',
      search: '<path d="M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-4-4"/>'
    }[name] || '';
    return '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' + p + '</svg>';
  }

  /* ---------- Instafeed (design .ig-grid, latest 6) ---------- */
  function fixInstafeedItem(el) {
    if (!el || !el.style) return;
    // Strip Instafeed float/% + padding-top aspect hacks so our grid cells size correctly on mobile.
    ['width', 'max-width', 'min-width', 'height', 'max-height', 'padding', 'padding-top',
      'margin', 'float', 'flex', 'flex-basis', 'aspect-ratio'].forEach(function (p) {
      el.style.removeProperty(p);
    });
  }

  /* Prefer a larger Instagram CDN derivative when Instafeed ships a tiny thumb
     (common: s150/s240/s320 displayed at ~200–400 CSS px → looks pixelated,
     especially on retina). Signature-safe: only rewrite size tokens; if the
     CDN rejects the upgrade, onerror restores the original src. */
  function largestFromSrcset(srcset) {
    if (!srcset) return '';
    var best = '';
    var bestW = 0;
    String(srcset).split(',').forEach(function (part) {
      var bits = part.trim().split(/\s+/);
      var u = bits[0] || '';
      var desc = bits[1] || '';
      var w = parseInt(desc, 10) || 0;
      if (/x$/i.test(desc)) w = Math.round(parseFloat(desc) * (window.devicePixelRatio || 1) * 100);
      if (u && w >= bestW) { bestW = w; best = u; }
      else if (u && !best) best = u;
    });
    return best;
  }

  function upgradeInstagramUrl(url) {
    if (!url || typeof url !== 'string') return '';
    if (!/cdninstagram|fbcdn\.net|instagram\.com/i.test(url)) return '';
    var next = url
      // Path: /s150x150/ /s320x320/ /s640x640/ → /s1080x1080/
      .replace(/\/s(?:150|240|320|480|640)x(?:150|240|320|480|640)\//gi, '/s1080x1080/')
      // Query stp=…s150x150 → …s1080x1080 (modern IG CDN)
      .replace(/([?&]stp=[^&]*?)_s(?:150|240|320|480|640)x(?:150|240|320|480|640)/gi, '$1_s1080x1080')
      .replace(/([?&]stp=[^&]*?)s(?:150|240|320|480|640)x(?:150|240|320|480|640)/gi, '$1s1080x1080');
    return next !== url ? next : '';
  }

  function pickHiResUrl(img, item) {
    var attrs = [
      'data-original', 'data-full', 'data-full-size', 'data-large', 'data-src-large',
      'data-hi-res', 'data-highres', 'data-standard', 'data-standard-resolution'
    ];
    var i, v;
    for (i = 0; i < attrs.length; i++) {
      v = img.getAttribute(attrs[i]);
      if (v) return v;
    }
    if (item) {
      for (i = 0; i < attrs.length; i++) {
        v = item.getAttribute(attrs[i]);
        if (v) return v;
      }
    }
    var fromSet = largestFromSrcset(img.getAttribute('srcset'));
    if (fromSet) {
      var upgradedSet = upgradeInstagramUrl(fromSet);
      return upgradedSet || fromSet;
    }
    var current = img.getAttribute('data-src') || img.currentSrc || img.src || '';
    return upgradeInstagramUrl(current) || '';
  }

  function applyHiRes(img, url) {
    if (!url || img.dataset.pdpHiRes === url) return;
    var previous = img.getAttribute('src') || '';
    var probe = new Image();
    probe.onload = function () {
      img.dataset.pdpHiRes = url;
      img.src = url;
      if (img.getAttribute('data-src')) img.setAttribute('data-src', url);
      // Drop tiny srcset so the browser doesn't keep preferring a 150w candidate.
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
    };
    probe.onerror = function () {
      if (previous && img.src !== previous) img.src = previous;
    };
    probe.src = url;
  }

  function fixInstafeedImg(img, item) {
    if (!img) return;
    if (img.style) {
      ['top', 'left', 'right', 'bottom', 'width', 'height', 'max-width', 'max-height',
        'transform', '-webkit-transform', 'margin'].forEach(function (p) {
        img.style.removeProperty(p);
      });
      // Re-assert after Instafeed's inline styles (mobile often sets translate(-50%,-50%)).
      img.style.setProperty('top', '0', 'important');
      img.style.setProperty('left', '0', 'important');
      img.style.setProperty('width', '100%', 'important');
      img.style.setProperty('height', '100%', 'important');
      img.style.setProperty('transform', 'translate(0)', 'important');
      img.style.setProperty('-webkit-transform', 'translate(0)', 'important');
      img.style.setProperty('object-fit', 'cover', 'important');
    }
    var hi = pickHiResUrl(img, item);
    if (hi) applyHiRes(img, hi);
  }

  function trimInstafeed(root) {
    $all('.ig-feed .instafeed-new-layout-wrapper, .ig-feed #insta-feed .instafeed-new-layout-wrapper', root).forEach(function (wrap) {
      Array.prototype.forEach.call(wrap.children, function (child, i) {
        if (i >= 6) {
          child.style.setProperty('display', 'none', 'important');
          return;
        }
        // Instafeed mobile layout (rows×cols) often hides posts 5–6; force the
        // same six tiles as desktop. Clear app hide classes/attrs too.
        child.removeAttribute('hidden');
        child.removeAttribute('aria-hidden');
        if (child.classList) {
          ['hidden', 'hide', 'instafeed-hidden', 'is-hidden', 'd-none'].forEach(function (c) {
            child.classList.remove(c);
          });
        }
        child.style.setProperty('display', 'block', 'important');
        child.style.setProperty('visibility', 'visible', 'important');
        child.style.setProperty('opacity', '1', 'important');
        fixInstafeedItem(child);
        $all('img, .js-lazy-image, video', child).forEach(function (media) {
          fixInstafeedImg(media, child);
        });
      });
    });
  }

  function initInstafeed(root) {
    root = root || document;
    if (!$('.ig-feed', root)) return;
    trimInstafeed(root);
    // App injects after its own script; re-trim when it signals ready + on late DOM mutations.
    if (!window.__pdpCsInstafeedBound) {
      window.__pdpCsInstafeedBound = true;
      document.addEventListener('instafeedAppLoaded', function () { trimInstafeed(document); });
    }
    $all('.ig-feed', root).forEach(function (feed) {
      if (!once(feed, 'instafeedObs')) return;
      if (!('MutationObserver' in window)) return;
      var mo = new MutationObserver(function () { trimInstafeed(feed); });
      mo.observe(feed, { childList: true, subtree: true });
    });
  }

  /* Shop Pay terms follow the visible variant. The element only refreshes
     when its variant-id attribute changes; global.js updates the hidden
     input and fires change, which does not reach that attribute. */
  function initPaymentTerms(root) {
    $all('.product--car-seat form.installment input[name="id"]', root).forEach(function (input) {
      if (!once(input, 'payterms')) return;
      function sync() {
        var terms = input.form && input.form.querySelector('shopify-payment-terms');
        if (terms && input.value) terms.setAttribute('variant-id', input.value);
      }
      input.addEventListener('change', sync);
    });
  }

  /* ---------- boot ---------- */
  function init(root) {
    root = root || document;
    initReveal(root);
    initAccordions(root);
    initFaqNav(root);
    initVideoButtons(root);
    initGalleryModal(root);
    initPhotoGrid(root);
    initColorGallery(root);
    initMediaDashes(root);
    initStickyBar(root);
    initCountdowns(root);
    initToasts(root);
    initInstockRow(root);
    initFinalCta(root);
    initJumpLinks(root);
    initCuratedRail(root);
    initCarousels(root);
    initInstafeed(root);
    initPaymentTerms(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);

  // Shopify theme editor: re-init the reloaded section
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  window.PDPCarSeat = { init: init };
})();
